import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router';
import { Loader2, AlertTriangle, UserPlus, CheckCircle2, Copy, Send, Server, Check, Sparkles } from 'lucide-react';
import {
  UniversalPage,
  Card,
  CardEyebrow,
  Button,
  Input,
  FormField,
  NotificationCard,
  BubbleGroup,
  useToast,
} from '@afrilink/ui';
import {
  salesService,
  type ActiveServicePlan,
  type DomainAvailability,
  type CreateCustomerOrderResult,
  type ActiveServiceSku,
} from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import {
  ADDON_SKUS,
  BUNDLE_ITEMS,
  STUDIO_DEPOSIT_PRESETS_CENTS,
  STUDIO_DEPOSIT_FLOOR_CENTS,
  STUDIO_TOTAL_CENTS,
  STUDIO_PLAN_MONTHS,
  installmentPreview,
  isDepositValid,
  zar,
  type StudioPlanMonths,
} from '@/domains/sales/studioQuotation';

/**
 * SalesAddCustomerPage — agent-driven "Add New Customer" (pay-first).
 *
 * Telephonic sales flow: capture name + email + mobile, pick a plan, optionally
 * search+bundle a domain, then send the customer a PayFast pay-link by email.
 * NO account/password is created for the customer here — a shadow account is
 * created silently and the customer sets a password only AFTER paying.
 *
 * Calls sales-order-create (Slice 2). Domain registration collects a compact
 * registrant (ZACR requires contact + address for .za registration).
 */

type FqdnMode = 'register' | 'existing' | 'later';

/**
 * What the agent is selling. Chosen FIRST, inline, as a bubble row — the page
 * never becomes a wizard (AC-8). Only the chosen branch's fields render.
 */
type ProductKind = 'hosting' | 'studio' | 'addon';

/** 'full' = pay R3,990 up front; otherwise a deposit + monthly plan. */
type DepositChoice = 'full' | 'other' | `${number}`;

const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
  'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
];

// Draft auto-save (2026-07-08). The agent fills this form during a live call —
// persist to sessionStorage on every change so a refresh, an accidental
// back-button, or a tab reload never loses what they've typed. Cleared on a
// successful submit. sessionStorage = per-tab, 24h TTL.
// v2 (2026-09-11): the form gained product / deposit / plan. A v1 draft restored
// into a v2 form would rehydrate a half-shaped state, so the key is bumped and
// old drafts are simply dropped rather than merged.
const DRAFT_KEY = 'sales_add_customer_draft_v2';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

interface Draft {
  name: string; email: string; phone: string; planSlug: string;
  fqdnMode: FqdnMode; registerFqdn: string; existingFqdn: string;
  addr1: string; city: string; province: string; postal: string;
  product: ProductKind; addonSku: string;
  deposit: DepositChoice; depositOtherRand: string; planMonths: StudioPlanMonths;
}

function loadDraft(): Partial<Draft> {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Draft & { savedAt?: number };
    if (Date.now() - (parsed.savedAt ?? 0) > DRAFT_TTL_MS) {
      sessionStorage.removeItem(DRAFT_KEY);
      return {};
    }
    return parsed;
  } catch { return {}; }
}
function saveDraft(d: Draft): void {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, savedAt: Date.now() })); } catch { /* storage disabled — non-fatal */ }
}
function clearDraft(): void {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* non-fatal */ }
}

export default function SalesAddCustomerPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const role = useStaffRole();

  // Restore an in-progress draft once (survives refresh / back-button / reload).
  const draftRef = useRef<Partial<Draft> | null>(null);
  if (draftRef.current === null) draftRef.current = loadDraft();
  const d = draftRef.current;

  // Customer
  const [name, setName] = useState(d.name ?? '');
  const [email, setEmail] = useState(d.email ?? '');
  const [phone, setPhone] = useState(d.phone ?? '');

  // What are we selling? (AC-8 — a bubble row, not a step)
  const [product, setProduct] = useState<ProductKind>(d.product ?? 'hosting');

  // Studio Starter
  const [deposit, setDeposit] = useState<DepositChoice>(d.deposit ?? '50000');
  const [depositOtherRand, setDepositOtherRand] = useState(d.depositOtherRand ?? '750');
  const [planMonths, setPlanMonths] = useState<StudioPlanMonths>(d.planMonths ?? 6);

  // One-off add-on / bundle. The catalogue's ORDER and the bundle pseudo-SKU
  // are curated here; the price and description come from the DB so the copy an
  // agent reads is the catalogue's own, not a stale hardcoded string.
  const [addonSku, setAddonSku] = useState(d.addonSku ?? ADDON_SKUS[0]!.code);
  const [skuRows, setSkuRows] = useState<ActiveServiceSku[]>([]);

  // Plan
  const [plans, setPlans] = useState<ActiveServicePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  // The plan load used to swallow its own error (`.catch(() => setPlansLoading(false))`),
  // which rendered an EMPTY plan card with no message and no retry — the agent
  // simply could not proceed and had nothing to report. Any failure here now
  // surfaces with a retry. See SALES_ADD_CUSTOMER_DIAGNOSIS_2026-07-29.md.
  const [plansError, setPlansError] = useState<string | null>(null);
  const [planSlug, setPlanSlug] = useState(d.planSlug ?? '');

  // Domain
  const [fqdnMode, setFqdnMode] = useState<FqdnMode>(d.fqdnMode ?? 'register');
  const [registerFqdn, setRegisterFqdn] = useState(d.registerFqdn ?? '');
  const [existingFqdn, setExistingFqdn] = useState(d.existingFqdn ?? '');
  const [availability, setAvailability] = useState<DomainAvailability | { status: 'idle' | 'checking' }>({ status: 'idle' });

  // Compact registrant (register mode only)
  const [addr1, setAddr1] = useState(d.addr1 ?? '');
  const [city, setCity] = useState(d.city ?? '');
  const [province, setProvince] = useState(d.province ?? 'Gauteng');
  const [postal, setPostal] = useState(d.postal ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateCustomerOrderResult | null>(null);

  // Persist the draft on every change (skip once submitted — the form is in its
  // success state and the draft has been cleared).
  useEffect(() => {
    if (result) return;
    saveDraft({
      name, email, phone, planSlug, fqdnMode, registerFqdn, existingFqdn,
      addr1, city, province, postal, product, addonSku, deposit, depositOtherRand, planMonths,
    });
  }, [name, email, phone, planSlug, fqdnMode, registerFqdn, existingFqdn, addr1, city, province,
      postal, product, addonSku, deposit, depositOtherRand, planMonths, result]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const rows = await salesService.listActivePlans();
      setPlans(rows);
      if (rows.length > 0) setPlanSlug((s) => s || rows[0]!.slug);
    } catch (err) {
      setPlans([]);
      setPlansError(err instanceof Error ? err.message : String(err));
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPlans();
    // Soft-fail: the curated list still renders with its own labels if this
    // errors, so a catalogue read must never block a sale.
    salesService.listActiveSkus().then(setSkuRows).catch(() => setSkuRows([]));
  }, [loadPlans]);

  /** Curated order + bundle, enriched with the DB's price and description. */
  const addonCards = useMemo(
    () =>
      ADDON_SKUS.map((sk) => {
        const row = skuRows.find((r) => r.code === sk.code);
        return {
          code: sk.code,
          label: row?.display_name ?? sk.label,
          price: row ? zar(row.price_cents) : sk.hint,
          description: row?.description ?? sk.hint,
        };
      }),
    [skuRows]
  );

  // Debounced domain availability (register mode)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (fqdnMode !== 'register') { setAvailability({ status: 'idle' }); return; }
    const fqdn = registerFqdn.trim().toLowerCase();
    if (!fqdn || !fqdn.includes('.')) { setAvailability({ status: 'idle' }); return; }
    setAvailability({ status: 'checking' });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const res = await salesService.checkDomain(fqdn);
      setAvailability(res);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fqdnMode, registerFqdn]);

  const selectedPlan = useMemo(() => plans.find((p) => p.slug === planSlug), [plans, planSlug]);

  // ── Derived: what this sale looks like ────────────────────────────────
  const depositCents =
    deposit === 'full' ? 0
    : deposit === 'other' ? Math.round((Number(depositOtherRand) || 0) * 100)
    : Number(deposit);
  const studioPlanMode = product === 'studio' && deposit !== 'full';
  const depositOk = !studioPlanMode || isDepositValid(depositCents);
  const preview = studioPlanMode ? installmentPreview(depositCents, planMonths) : null;

  // Hosting and Studio both register a domain; an add-on never does.
  const domainApplies = product === 'hosting' || product === 'studio';

  // Paid gTLDs (anything but free .co.za) require a full postal address up-front.
  const isPaidGtld =
    domainApplies &&
    fqdnMode === 'register' &&
    registerFqdn.trim().includes('.') &&
    !registerFqdn.trim().toLowerCase().endsWith('.co.za');

  // Only a PAID gTLD needs the address up-front — the registry mandates it and
  // there is nothing to fall back to. A free .co.za never does, on any product:
  // a blank field is filled with SightHost's company address (owner decision
  // 2026-07-10, reaffirmed 2026-09-11 after requiring it on Studio blocked an
  // agent mid-call). Name, email and phone are always the customer's.
  const addressRequired = isPaidGtld;
  const addrLabel = (base: string) => (addressRequired ? base : `${base} (optional)`);

  const validationError = useMemo(() => {
    if (!name.trim()) return 'Customer name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'A valid customer email is required.';
    if (!phone.trim()) return 'A contact number is required.';

    if (product === 'hosting' && !planSlug) return 'Choose a hosting plan.';
    if (product === 'addon' && !addonSku) return 'Choose a service.';
    if (studioPlanMode && !isDepositValid(depositCents)) {
      return `Deposit must be between ${zar(STUDIO_DEPOSIT_FLOOR_CENTS)} and ${zar(STUDIO_TOTAL_CENTS - 1)}.`;
    }

    if (domainApplies && fqdnMode === 'register') {
      if (!registerFqdn.trim().includes('.')) return 'Enter a domain to register (e.g. business.co.za).';
      if ('status' in availability && availability.status === 'taken') return `${registerFqdn.trim()} is already taken — pick another.`;
      if (product === 'studio' && !registerFqdn.trim().toLowerCase().endsWith('.co.za')) {
        return 'Studio Starter includes a free .co.za only — register another TLD as a separate order.';
      }
      if (addressRequired) {
        if (!addr1.trim()) return 'Street address is required for this domain.';
        if (!city.trim()) return 'City is required for this domain.';
        if (!postal.trim()) return 'Postal code is required for this domain.';
      }
    }
    if (domainApplies && fqdnMode === 'existing' && !existingFqdn.trim().includes('.')) {
      return "Enter the customer's existing domain.";
    }
    return null;
  }, [name, email, phone, product, planSlug, addonSku, studioPlanMode, depositCents,
      domainApplies, fqdnMode, registerFqdn, existingFqdn, availability, addressRequired,
      addr1, city, postal]);

  function normalisePhone(p: string): string {
    const digits = p.replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) return `+${digits.slice(1).slice(0, 4)}.${digits.replace(/^\+\d{1,4}/, '')}`;
    if (digits.startsWith('0')) return `+27.${digits.slice(1)}`;
    return `+27.${digits}`;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (validationError) { setError(validationError); return; }
    setSubmitting(true);
    setError(null);
    try {
      const registrant = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        contactNumber: normalisePhone(phone),
        address1: addr1.trim(),
        city: city.trim(),
        province,
        postalCode: postal.trim(),
        country: 'ZA',
      };

      if (product === 'hosting') {
        // Unchanged path: sales-order-create → invoice + PayFast pay-link.
        const res = await salesService.createCustomerOrder({
          customer_email: email.trim().toLowerCase(),
          customer_name: name.trim(),
          customer_phone: phone.trim(),
          plan_slug: planSlug,
          fqdn_mode: fqdnMode,
          register_fqdn: fqdnMode === 'register' ? registerFqdn.trim().toLowerCase() : undefined,
          existing_fqdn: fqdnMode === 'existing' ? existingFqdn.trim().toLowerCase() : undefined,
          register_registrant: fqdnMode === 'register' ? registrant : undefined,
        });
        clearDraft();
        setResult(res);
        toast({
          tone: 'success',
          title: res.customer_created ? 'Customer added — pay-link sent' : 'Order created — pay-link sent',
          body: `PayFast link emailed to ${res.emailed_to}.`,
        });
        return;
      }

      // Studio + add-ons → sales-service-order-create with new_customer, which
      // issues a QUO- quotation (AC-2). The EF resolves an existing profile by
      // email itself, so the agent never chooses between the two paths.
      const bundleItems = BUNDLE_ITEMS[addonSku];
      const quote = await salesService.issueQuotation({
        new_customer: { email: email.trim().toLowerCase(), name: name.trim(), phone: phone.trim() },
        ...(product === 'studio'
          ? {
              sku_code: 'studio-starter',
              ...(studioPlanMode
                ? { plan_months: planMonths, deposit_amount_cents: depositCents }
                : {}),
              // G6: the bundled free .co.za, captured now and registered only
              // once the deposit clears.
              fqdn_mode: fqdnMode,
              ...(fqdnMode === 'register'
                ? { register_fqdn: registerFqdn.trim().toLowerCase(), register_registrant: registrant }
                : {}),
              ...(fqdnMode === 'existing'
                ? { existing_fqdn: existingFqdn.trim().toLowerCase() }
                : {}),
            }
          : bundleItems
            ? { items: bundleItems }
            : { sku_code: addonSku }),
      });

      clearDraft();
      setResult({
        ok: true,
        customer_id: '',
        customer_created: quote.customer_created ?? true,
        invoice_id: quote.invoice_id,
        invoice_number: quote.quote_number,
        pay_url: quote.pay_url,
        total_zar: quote.total_zar,
        emailed_to: quote.emailed_to,
      });
      toast({
        tone: 'success',
        title: `Quotation ${quote.quote_number} sent`,
        body: `Emailed to ${quote.emailed_to}. Paying it accepts the quotation.`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (role.loading) {
    return <UniversalPage title="Add customer"><Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card></UniversalPage>;
  }
  if (!role.isSalesStaff && !role.isAdmin) {
    return (
      <UniversalPage title="Add customer">
        <NotificationCard tone="danger" title="Not authorised" body="Only sales staff or admins can add customers." />
      </UniversalPage>
    );
  }

  // ── Success state ──────────────────────────────────────────────────
  if (result) {
    return (
      <UniversalPage
        title="Customer added"
        breadcrumbs={[{ label: 'Sales', href: '/sales' }, { label: 'Add customer' }]}
      >
        <Card className="space-y-4 p-6">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-success" />
            <div>
              <h3 className="text-base font-semibold text-foreground">
                {result.customer_created ? 'New customer created' : 'Order added to existing customer'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {product === 'hosting' ? 'PayFast pay-link' : 'Quotation'} emailed to{' '}
                <strong>{result.emailed_to}</strong>. They pay first, then set their password automatically.
              </p>
            </div>
          </div>

          <div className="rounded border border-border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between py-1"><span className="text-muted-foreground">{product === 'hosting' ? 'Invoice' : 'Quotation'}</span><span className="font-medium">{result.invoice_number}</span></div>
            <div className="flex justify-between py-1"><span className="text-muted-foreground">Amount</span><span className="font-medium">R{result.total_zar.toFixed(2)}</span></div>
          </div>

          <FormField label="Pay-link (share directly via WhatsApp/SMS if needed)" htmlFor="paylink">
            <div className="flex gap-2">
              <Input id="paylink" readOnly value={result.pay_url} onFocus={(e) => e.currentTarget.select()} />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  navigator.clipboard?.writeText(result.pay_url);
                  toast({ tone: 'success', title: 'Copied', body: 'Pay-link copied to clipboard.' });
                }}
              >
                <Copy className="h-4 w-4" /> Copy
              </Button>
            </div>
          </FormField>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setResult(null); setName(''); setEmail(''); setPhone(''); setRegisterFqdn(''); setExistingFqdn(''); setAddr1(''); setCity(''); setPostal(''); }}>
              Add another
            </Button>
            <Button onClick={() => navigate('/sales/customers')}>Done</Button>
          </div>
        </Card>
      </UniversalPage>
    );
  }

  const avail = availability.status;

  return (
    <UniversalPage
      title="Add customer"
      description="Capture the customer, pick what they bought, and send them a pay-link or quotation by email. No password needed — they set one after paying."
      breadcrumbs={[{ label: 'Sales', href: '/sales' }, { label: 'Add customer' }]}
      actions={<Link to="/sales"><Button variant="outline" size="sm" disabled={submitting}>Cancel</Button></Link>}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Customer */}
        <Card className="space-y-4 p-6">
          <h3 className="text-sm font-semibold text-foreground">Customer details</h3>
          <FormField label="Full name" htmlFor="name" required>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} placeholder="Jane Mokoena" maxLength={200} />
          </FormField>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Email" htmlFor="email" required helper="The pay-link + welcome email go here.">
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} placeholder="jane@example.co.za" />
            </FormField>
            <FormField label="Mobile number" htmlFor="phone" required helper="South African format: 0… or +27…">
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={submitting} placeholder="082 123 4567" />
            </FormField>
          </div>
        </Card>

        {/* What are we selling? — bubbles, inline, never a step (AC-8) */}
        <Card className="space-y-4 p-6">
          <h3 className="text-sm font-semibold text-foreground" id="product-label">What are they buying?</h3>
          <BubbleGroup
            aria-labelledby="product-label"
            value={product}
            onValueChange={(v) => setProduct(v as ProductKind)}
            disabled={submitting}
            options={[
              { value: 'hosting', label: 'Hosting', hint: 'Monthly plan' },
              { value: 'studio', label: 'Studio Starter', hint: 'R3,990 website' },
              { value: 'addon', label: 'Add-on service', hint: 'Care, Site Boost, Logo…' },
            ]}
          />

          {/* ── Hosting ─────────────────────────────────────────────── */}
          {product === 'hosting' && (
            plansLoading ? (
              <p className="text-sm text-muted-foreground">Loading plans…</p>
            ) : plansError ? (
              <div className="rounded border border-danger/30 bg-danger/5 p-3 text-sm">
                <p className="font-medium text-danger">Could not load the hosting plans.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  You cannot create the order without a plan. Try again — if it keeps failing,
                  send this message to support: <span className="font-mono">{plansError}</span>
                </p>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => void loadPlans()}>
                  Retry
                </Button>
              </div>
            ) : plans.length === 0 ? (
              <div className="rounded border border-warning/30 bg-warning/5 p-3 text-sm">
                <p className="font-medium text-foreground">No active hosting plans are available.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Nothing failed — the catalogue is genuinely empty. An admin needs to activate a
                  plan under Settings before an order can be created.
                </p>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => void loadPlans()}>
                  Refresh
                </Button>
              </div>
            ) : (
              // The plan grid is the SAME selectable card NewServicePage Step 1
              // uses — shared Card + CardEyebrow, not a pill and not a
              // hand-rolled <label>. A plan is chosen on what it INCLUDES, so
              // the description line is load-bearing, not decoration.
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {plans.map((pl, i) => {
                  const selected = pl.slug === planSlug;
                  return (
                    <Card key={pl.id} tone="hairline" featured={selected} interactive>
                      <CardEyebrow index={i + 1}>Plan</CardEyebrow>
                      <div className="flex h-full flex-col px-6 pb-6 pt-3">
                        <div className="mb-3 flex items-center gap-2 font-display text-base font-bold tracking-tight text-navy-primary">
                          <Server className="size-4 text-primary" aria-hidden /> {pl.name}
                        </div>
                        <div className="mb-1 font-display text-3xl font-bold tabular-nums text-navy-primary">
                          R {Number(pl.recurring_amount).toFixed(0)}
                          <span className="ml-1 font-mono text-xs font-normal uppercase tracking-wider text-muted-foreground">/ month</span>
                        </div>
                        <p className="mb-6 flex-1 text-xs leading-relaxed text-muted-foreground">{pl.description}</p>
                        <Button
                          type="button"
                          variant={selected ? 'marketing' : 'marketing-ghost'}
                          className="text-xs"
                          disabled={submitting}
                          onClick={() => setPlanSlug(pl.slug)}
                        >
                          {selected ? 'Selected' : 'Select'} {selected && <Check className="size-4" aria-hidden />}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )
          )}

          {/* ── Studio Starter: deposit + plan, both as bubbles (AC-9) ── */}
          {product === 'studio' && (
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-medium text-foreground" id="deposit-label">Deposit</p>
                <BubbleGroup
                  aria-labelledby="deposit-label"
                  value={deposit}
                  onValueChange={(v) => setDeposit(v as DepositChoice)}
                  disabled={submitting}
                  options={[
                    { value: 'full', label: 'Pay in full', hint: zar(STUDIO_TOTAL_CENTS) },
                    ...STUDIO_DEPOSIT_PRESETS_CENTS.map((c) => ({
                      value: String(c) as DepositChoice,
                      label: zar(c),
                    })),
                    { value: 'other', label: 'Other' },
                  ]}
                />
                {deposit === 'other' && (
                  <div className="mt-3 max-w-xs">
                    <FormField label="Deposit amount (R)" htmlFor="deposit_other">
                      <Input
                        id="deposit_other"
                        type="number"
                        min={STUDIO_DEPOSIT_FLOOR_CENTS / 100}
                        max={(STUDIO_TOTAL_CENTS - 100) / 100}
                        step={50}
                        value={depositOtherRand}
                        onChange={(e) => setDepositOtherRand(e.target.value)}
                        disabled={submitting}
                      />
                    </FormField>
                    {!depositOk && (
                      <p className="mt-1 text-xs text-danger">
                        Between {zar(STUDIO_DEPOSIT_FLOOR_CENTS)} and {zar(STUDIO_TOTAL_CENTS - 100)}.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {studioPlanMode && (
                <div>
                  <p className="mb-2 text-xs font-medium text-foreground" id="plan-months-label">Pay the balance over</p>
                  <BubbleGroup
                    aria-labelledby="plan-months-label"
                    value={String(planMonths)}
                    onValueChange={(v) => setPlanMonths(Number(v) as StudioPlanMonths)}
                    disabled={submitting}
                    options={STUDIO_PLAN_MONTHS.map((m) => ({ value: String(m), label: `${m} months` }))}
                  />
                </div>
              )}

              {preview && (
                <div className="rounded border border-border bg-muted/40 p-3 text-sm">
                  <div className="flex justify-between py-0.5">
                    <span className="text-muted-foreground">Studio Starter</span>
                    <span>{zar(STUDIO_TOTAL_CENTS)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-muted-foreground">Deposit now</span>
                    <span>{zar(depositCents)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-muted-foreground">Then {planMonths} × monthly</span>
                    <span className="font-semibold">{zar(preview.installmentCents)}</span>
                  </div>
                  {preview.roundingCents > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Includes a {zar(preview.roundingCents)} rounding discount.
                    </p>
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Includes a free .co.za for the first year and 3 months of hosting.
                Nothing is registered until the deposit is paid.
              </p>
            </div>
          )}

          {/* ── One-off add-on / bundle ─────────────────────────────── */}
          {product === 'addon' && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {addonCards.map((sk, i) => {
                const selected = sk.code === addonSku;
                return (
                  <Card key={sk.code} tone="hairline" featured={selected} interactive>
                    <CardEyebrow index={i + 1}>Service</CardEyebrow>
                    <div className="flex h-full flex-col px-6 pb-6 pt-3">
                      <div className="mb-2 flex items-center gap-2 font-display text-base font-bold tracking-tight text-navy-primary">
                        <Sparkles className="size-4 text-primary" aria-hidden /> {sk.label}
                      </div>
                      <div className="mb-1 font-display text-2xl font-bold tabular-nums text-navy-primary">
                        {sk.price}
                      </div>
                      <p className="mb-6 flex-1 text-xs leading-relaxed text-muted-foreground">{sk.description}</p>
                      <Button
                        type="button"
                        variant={selected ? 'marketing' : 'marketing-ghost'}
                        className="text-xs"
                        disabled={submitting}
                        onClick={() => setAddonSku(sk.code)}
                      >
                        {selected ? 'Selected' : 'Select'} {selected && <Check className="size-4" aria-hidden />}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>

        {/* Domain — hosting + Studio only; an add-on registers nothing. */}
        {domainApplies && (
        <Card className="space-y-4 p-6">
          <h3 className="text-sm font-semibold text-foreground" id="domain-label">Domain</h3>
          <BubbleGroup
            aria-labelledby="domain-label"
            value={fqdnMode}
            onValueChange={(v) => setFqdnMode(v as FqdnMode)}
            disabled={submitting}
            options={[
              { value: 'register', label: 'Register a new domain' },
              { value: 'existing', label: 'Customer has a domain' },
              { value: 'later', label: 'Decide later' },
            ]}
          />

          {fqdnMode === 'register' && (
            <>
              <FormField
                label="Domain to register"
                htmlFor="register_fqdn"
                helper={product === 'studio'
                  ? '.co.za is included FREE for the first year with Studio Starter.'
                  : '.co.za is FREE for the first year with any hosting plan.'}
              >
                <Input id="register_fqdn" value={registerFqdn} onChange={(e) => setRegisterFqdn(e.target.value)} disabled={submitting} placeholder="business.co.za" />
              </FormField>
              {avail === 'checking' && <p className="text-xs text-muted-foreground">Checking availability…</p>}
              {avail === 'available' && <p className="text-xs font-medium text-success">✓ {registerFqdn.trim()} is available.</p>}
              {avail === 'taken' && <p className="text-xs font-medium text-danger">✗ {registerFqdn.trim()} is taken — try another.</p>}
              {avail === 'tld_not_supported' && <p className="text-xs font-medium text-warning">That TLD isn't supported for self-registration.</p>}
              {avail === 'premium' && <p className="text-xs font-medium text-warning">Premium domain — handle via a domain ticket.</p>}
              {avail === 'invalid' && <p className="text-xs font-medium text-warning">That doesn't look like a valid domain.</p>}
              {/* The availability EF answers 400 for a half-typed name, and the
                  debounce fires as soon as the text contains a dot — so this
                  state is common and used to render NOTHING at all: the agent
                  saw "Checking availability…" and then silence. */}
              {avail === 'error' && (
                <p className="text-xs font-medium text-muted-foreground">
                  Could not check that name yet — keep typing, or try again in a moment.
                </p>
              )}

              <p className="text-xs text-muted-foreground">
                {isPaidGtld
                  ? 'A full postal address is required for this domain.'
                  : "Address is optional for .co.za — leave it blank and SightHost's company address is used."}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label={addrLabel('Address line')} htmlFor="addr1">
                  <Input id="addr1" value={addr1} onChange={(e) => setAddr1(e.target.value)} disabled={submitting} placeholder="12 Main Road" required={addressRequired} />
                </FormField>
                <FormField label={addrLabel('City')} htmlFor="city">
                  <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} disabled={submitting} placeholder="Johannesburg" required={addressRequired} />
                </FormField>
                <FormField label="Province" htmlFor="province">
                  <select id="province" value={province} onChange={(e) => setProvince(e.target.value)} disabled={submitting} className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                    {SA_PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </FormField>
                <FormField label={addrLabel('Postal code')} htmlFor="postal">
                  <Input id="postal" value={postal} onChange={(e) => setPostal(e.target.value)} disabled={submitting} placeholder="2000" required={addressRequired} />
                </FormField>
              </div>
            </>
          )}

          {fqdnMode === 'existing' && (
            <FormField label="Existing domain" htmlFor="existing_fqdn" helper="We'll point their hosting at this domain. They keep it wherever it's registered.">
              <Input id="existing_fqdn" value={existingFqdn} onChange={(e) => setExistingFqdn(e.target.value)} disabled={submitting} placeholder="theirdomain.co.za" />
            </FormField>
          )}

          {fqdnMode === 'later' && (
            <p className="text-sm text-muted-foreground">
              {product === 'studio'
                ? 'The domain is chosen at the consultation. Note that nothing is registered automatically on this route — the included .co.za is attached by hand.'
                : 'A temporary URL is used until the customer chooses a domain. They can add one anytime.'}
            </p>
          )}
        </Card>
        )}

        {error && (
          <NotificationCard
            tone="danger"
            title="Could not add customer"
            body={<span className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span></span>}
          />
        )}

        {validationError && !error && (
          <p className="text-right text-xs text-warning" role="status">
            {validationError}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          <p className="text-sm text-muted-foreground">
            {product === 'hosting' && selectedPlan && (
              <>Invoice total: <strong>R{Number(selectedPlan.recurring_amount).toFixed(2)}</strong>
              {fqdnMode === 'register' && registerFqdn.trim().endsWith('.co.za') ? ' (domain free 1st year)' : ''}</>
            )}
            {/* A payment plan bills the DEPOSIT now, not R3,990 — showing the
                full price here would misstate what the customer is about to pay. */}
            {product === 'studio' && (
              studioPlanMode && preview
                ? <>Quotation: <strong>{zar(depositCents)}</strong> deposit, then {planMonths} × {zar(preview.installmentCents)}</>
                : <>Quotation: <strong>{zar(STUDIO_TOTAL_CENTS)}</strong> paid in full</>
            )}
            {product === 'addon' && <>Quotation for <strong>{addonCards.find((a) => a.code === addonSku)?.label}</strong></>}
          </p>
          <div className="flex gap-2">
            <Link to="/sales"><Button variant="outline" disabled={submitting}>Cancel</Button></Link>
            {/*
              The button stays ENABLED while the form is incomplete and the
              reason is shown beside it. A greyed-out submit with no explanation
              is a dead end: the agent is on a call, cannot see which of four
              branches is unsatisfied, and has nothing to report. Clicking now
              surfaces the exact blocker instead.
            */}
            <Button type="submit" disabled={submitting} loading={submitting}>
              {submitting
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                : <><Send className="h-4 w-4" /> Add customer &amp; send {product === 'hosting' ? 'pay-link' : 'quotation'}</>}
            </Button>
          </div>
        </div>
      </form>
    </UniversalPage>
  );
}
